uniform float uTime;
uniform sampler2D uTexture;
uniform vec4 uColorDead;

// Particles can have color gradient over lifetime defined via texture
uniform sampler2D uColors;
uniform lowp int uColorsCount;
uniform sampler2D uColorStops;
uniform lowp int uColorStopCount;

varying vec2 vUV;
varying vec4 vColor;
varying float vIndex;
varying float vLifetime;
varying float vAge;
varying float vAgeFade;
varying vec2 vSpriteUvOffset;
varying vec2 vSpriteUvScale;

// Every particle's color gradient is stored in the texture sequentially, where
// a pixel represents a color stop. For example, [0,0] represents the first
// particle's first color stop. If we know each pixel has 3 color stops, then
// [1,0] represents the first particle's second color stop, and [3,0] represents
// the first particle's third color stop. [4,0] would then start the second
// particle's color sequence. The texture wraps after 512 pixels.
ivec2 colorIndexToTexel(lowp int index) {
    int width = 512;
    int x = index % width;
    int y = index / width;
    return ivec2(x, y);
}

// Similar to above, but instead of each pixel representing an RGBA color, it
// represents the percentage stop (0.0 to 1.0) in the gradient.
ivec2 colorStopIndexToTexel(lowp int index) {
    return ivec2(index, 0);
}

void main() {
    if(vAge < 0.0 || vAge > vLifetime) {
        return;
    }

    lowp int particle = int(vIndex);
    vec4 color = vColor;
    float progress = vAge / vLifetime;

    // Apply sprite sheet UV transformation
    vec2 spriteUv = vUV * vSpriteUvScale + vSpriteUvOffset;
    vec4 textureColor = texture(uTexture, spriteUv);

    // Use gradient colors
    if(uColorStopCount > 0) {
        // Combine gradient color with texture
        lowp int leftBound = 0;
        lowp int rightBound = 0;

        for(lowp int i = 0; i < uColorStopCount; i++) {
            float stop = texelFetch(uColorStops, colorStopIndexToTexel(i), 0).r;
            if(progress >= stop) {
                leftBound = i;
            }
        }
        rightBound = min(leftBound + 1, uColorStopCount - 1);

        lowp int uvIndex = particle * uColorStopCount;

        vec4 colorA = texelFetch(uColors, colorIndexToTexel(uvIndex + leftBound), 0);
        vec4 colorB = texelFetch(uColors, colorIndexToTexel(uvIndex + rightBound), 0);

        float stopA = texelFetch(uColorStops, colorStopIndexToTexel(leftBound), 0).r;
        float stopB = texelFetch(uColorStops, colorStopIndexToTexel(rightBound), 0).r;

        if(stopA == stopB) {
            color = colorA;
        } else {
            float localProgress = (progress - stopA) / (stopB - stopA);
            color = mix(colorA, colorB, localProgress);
        }
    } else {
        color = mix(vColor, uColorDead, progress);
    }

    // Determine visibility: use alpha if available, otherwise derive from brightness.
    // Some textures (stars, beams) have black backgrounds instead of alpha transparency,
    // so we use brightness as visibility to make the black areas transparent.
    float textureBrightness = max(max(textureColor.r, textureColor.g), textureColor.b);
    bool hasRealAlpha = textureColor.a != 1.0;
    float textureVisibility = hasRealAlpha ? textureColor.a : textureBrightness;

    float finalAlpha = color.a * vAgeFade * textureVisibility;

    vec4 finalColor = vec4(textureColor.rgb * color.rgb, textureColor.a);
    gl_FragColor = vec4(finalColor.rgb, finalAlpha);
    
    // 3js recommends including these for proper color management
    #include <tonemapping_fragment>
    #include <colorspace_fragment>
}
